import {Component} from '@angular/core';
import {AlertController} from '@ionic/angular';
import {TranslateService} from '@ngx-translate/core';
import * as moment from 'moment';
import { zip} from 'rxjs';
import {finalize} from 'rxjs/operators';


import {UserContent, UserProfile} from 'app/profile/model/model';
import {UserService} from 'app/profile/services/user.service';
import {getTranslation} from 'shared/helpers/get-translation';
import {NavService} from 'shared/nav/nav.service';

import { DefaultPage } from 'app/menu/components/default-page/default-page';
import { CModule } from 'app/cmodule/model/model';
import { CModuleService } from 'app/cmodule/services/cmodule.service';
import { Content, ContentService } from 'app/content';
import { Dialog, DialogsService } from 'app/dialogs';

const LABEL_FORMAT: string = 'dddd, DD.MM.YYYY';

@Component({
  selector: 'cmodule-page',
  templateUrl: 'cmodule-page.html',
  styleUrls: ['cmodule-page.scss'],
})
export class CModulePage extends DefaultPage {

  public isLoading: boolean = false;
  public cmodules: Array<CModule> = [];
  private userContents: Array<UserContent> = [];

  public isLeader: boolean = true;

  public showLeaderContent: boolean = false; // show leader cmodule despite you are no leader
  
  public selectedCModuleId: string;

  // [cmodule._id][content._id]
  public contentExpanded: Dictionary<boolean> = {};

  public warnEn: number = 0;

  constructor(
    private navService: NavService,
    private translateService: TranslateService,
    private cmoduleService: CModuleService,
    private contentService: ContentService,
    private dialogsService: DialogsService,
    private userService: UserService,
    private alertCtrl: AlertController,
  ) {
    super(navService);
  }

  public get hasCModules(): boolean {
    return this.cmodules.length > 0;
  }

  private ionViewWillEnter(): void {
    this.isLoading = true;
    this.load();
  }

  private ionViewWillLeave(): void {
    // reset all "new" marks
  }

  private load(): void {
    this.cmoduleService.getCModules('list')
      .pipe(finalize(() => { this.isLoading = false; }))
      .subscribe((cmodules: Array<CModule>) => {
        this.cmodules = cmodules.sort((a, b) => a.orderno - b.orderno);
        this.mergeUserContent();
        if (this.cmodules.length > 0 && this.selectedCModuleId === undefined) {
          setTimeout(() => { this.selectedCModuleId = this.cmodules[0]._id + ''; });
        }
      });
    this.userService.getUserProfile('settings')
      .subscribe((profile: UserProfile) => {
        if (profile.settings && profile.settings.leading) {
            this.isLeader = profile.settings.leading.leading;
            this.showLeaderContent = profile.settings.showLeaderContent;
          }
        });
    this.contentService.getContents('list')
      .subscribe((userContents: Array<UserContent>) => {
          this.userContents = userContents;
          this.mergeUserContent();
      });
  }

  public selectCModule(cmodule: CModule): void {
    if (this.selectedCModuleId == cmodule._id + '') {
      this.selectedCModuleId = undefined;
    }
    else {
      this.selectedCModuleId = cmodule._id + '';
    }
  }

  public trackCModules(index: number, cmodule: CModule): string {
    return cmodule._id;
  }

  public trackContents(index: number, content: Content): string {
    return content._id;
  }

  public getLocalizedCModuleText(text: Dictionary<string>): string {
    const res = getTranslation(text, this.translateService);
    if (res == '' && this.translateService.currentLang != 'de') {
      if ( this.warnEn == 0 ) {
        setTimeout(() => {
          if ( this.warnEn == 0 ) {
            this.warnEn = 1;
            setTimeout(() => {this.warnEn = 2;}, 3000);
          }; }, 1000);
      }
    }
    return getTranslation(text, this.translateService);
  }

  public getCModuleClass(cmodule: CModule): string {
    return 'item-level-3 state cmodule-body '; // + userCModule.state + (this.selUserCModule != null && this.selUserCModule._id == userCModule._id ? ' selected' : '') + (userCModule.suggest != undefined && userCModule.suggest > 0 ? ' suggested' : '');
  }

  public formateDate(date: Date): string {
    return moment(date).format('DD.MM.YYYY');
  }

  public start(userContent: UserContent, event: Event): void {
    if ( event !== undefined ) {
      event.preventDefault();
      event.stopImmediatePropagation();
    }

    this.contentService.start(userContent)
      .subscribe(() => {
        // display
        this.contentService.getContent(userContent)
        .subscribe((userContent2) => {
          this.showContent(userContent2);
        });
      });
  }

  public pause(userContent: UserContent, event: Event): void {
    if ( event !== undefined ) {
      event.preventDefault();
      event.stopImmediatePropagation();
    }

    this.contentService.pause(userContent)
      .subscribe(() => {
        // hier reicht ein load
        this.load();
      });
  }

  public resume(userContent: UserContent, event: Event): void {
    if ( event !== undefined ) {
      event.preventDefault();
      event.stopImmediatePropagation();
    }

    this.contentService.resume(userContent)
      .subscribe(() => {
        // hier reicht ein load
        this.load();
      });
  }

  public restart(userContent: UserContent, event: Event): void {
    if ( event !== undefined ) {
      event.preventDefault();
      event.stopImmediatePropagation();
    }

    this.showRestartAlert(userContent);
  }

  private doRestart(userContent: UserContent, runNow: boolean): void {
    this.contentService.restart(userContent)
      .subscribe(() => {
        // recalc
        this.contentService.calculateNewContents()
          .subscribe((cnt) => {
            this.userService.setNewContentCount(cnt);
            this.load();
          });
        // display
        if ( runNow ) {
          this.contentService.getContent(userContent)
          .subscribe((userContent2) => {
            this.showContent(userContent2);
          });
        }
      });
  }

  public getPdf(cmodule: CModule, event: Event): void {
    if ( event !== undefined ) {
      event.preventDefault();
      event.stopImmediatePropagation();
    }
    this.contentService.getPdf(cmodule._id, this.getLocalizedCModuleText(cmodule.title), 'cmodule')
    .then((info) => {
      console.log(info);
    });
  }

  private showErrorAlert(s: string): void {
    zip(
      this.translateService.get('COMMON.ERROR'),
      this.translateService.get('COMMON.OK'),
      this.translateService.get('CONTENT.COULD_NOT_PURCHASE'),
    ).subscribe(async(translations) => {
      const alert: HTMLIonAlertElement = await this.alertCtrl.create({
        subHeader: translations[0],
        buttons: [translations[1]],
        // tslint:disable-next-line:no-magic-numbers
        message: translations[2] + ' ' + s,
      });
      alert.present();
    });
  }

  public showContent(userContent: UserContent) {
    if (userContent != undefined && userContent.state >= 5) {
      if ( userContent.state == 9) {
        this.navService.push('cmodule/flow', { userContent: userContent });
        return;
      }
      // load matching Dialog
      this.dialogsService.getDialogs()
      .subscribe((dialogs) => { 
        const cand: Array<Dialog> = [];
        for(let i = 0; i < dialogs.length; i++) {
          if ( dialogs[i].contentInfo !== undefined && dialogs[i].contentInfo.uc_id == userContent._id ) {
            cand.push(dialogs[i]);
          }
        }
        if ( cand.length > 0 ) {
          const dia = cand.reduce((acc: Dialog, cur:Dialog) => acc == null || acc.contentInfo.pos > cur.contentInfo.pos ? cur : acc, null);
          this.navService.push('content/flow', { dialog: dia });
        } else {
          // otherwise no active dialog, so just display
          this.navService.push('content/flow', { userContent: userContent });
        }
      });
    }
  }

  public getUcState(cmodule:CModule, i: number) {
    return cmodule.userContents !== undefined && cmodule.userContents[i] !== undefined ? cmodule.userContents[i].state : undefined;
  }

  public getUc(cmodule:CModule, i: number, attr?: string) {
    if ( attr !== undefined ) {
      return cmodule.userContents !== undefined && cmodule.userContents[i] !== undefined ? cmodule.userContents[i][attr] : undefined;
    }
    return cmodule.userContents !== undefined ? cmodule.userContents[i]  : undefined;
  }

  public getProgress(userContent: UserContent): string {
    if ( userContent !== undefined ) {
      const length = userContent.regularLength !== undefined ? userContent.regularLength : userContent.contentFull.items.length;
      if ( userContent.curContentPos !== undefined ) {
        return '(' + Math.min(userContent.curContentPos, length) + '/' + length + ')';
      }
    }
    return '';
  }

  private mergeUserContent(): void {
    if (this.cmodules.length > 0 && this.userContents.length > 0) {
      const userContentsMap = {};
      this.userContents.forEach((uc) => { userContentsMap[uc.content] = uc; });
      this.cmodules.forEach((cm) => {
        cm.userContents = cm.contents.map(cmId => userContentsMap[cmId]);
      });
    }
  }

  private showRestartAlert(userContent: UserContent): void {

    this.translateService.get([
      'CONTENT.RESTART_ALERT.TITLE',
      'CONTENT.RESTART_ALERT.MESSAGE',
      'COMMON.NO',
      'CONTENT.RESTART_ALERT.BUTTON_STAY',
      'CONTENT.RESTART_ALERT.BUTTON_GO',
    ]).subscribe(async(translations) => {
      const titleLabel: string = translations['CONTENT.RESTART_ALERT.TITLE'];
      const message: string = translations['CONTENT.RESTART_ALERT.MESSAGE'];
      const noLabel: string = translations['COMMON.NO'];
      const stayLabel: string = translations['CONTENT.RESTART_ALERT.BUTTON_STAY'];
      const goLabel: string = translations['CONTENT.RESTART_ALERT.BUTTON_GO'];

      const alert: HTMLIonAlertElement = await this.alertCtrl.create({
        cssClass: 'start-content-alert-class',
        header: titleLabel,
        message: message,
        buttons: [
          {
            text: noLabel,
            role: 'cancel',
          },
          {
            text: stayLabel,
            handler: (): void => {
              this.doRestart(userContent, false);
            },
          },
          {
            text: goLabel,
            handler: (): void => {
              this.doRestart(userContent, true);
            },
          },
        ],
      });
      alert.present();
    });
  }

}
